import { Box, type TableCellProps } from '@mui/material';
import { type PodcastType } from '@prisma/client';
import { defineMessage, type MessageDescriptor, type IntlShape } from 'react-intl';
import { type RankChange } from './types';
import { ActivePodcasts } from 'components/ActivePodcasts';
import { FormattedNumber } from 'components/FormattedNumber';
import { ImageFade } from 'components/ImageFade';
import { RankChange as RankChangeComponent } from 'components/RankChange';
import { type FilterState } from 'components/ReportTable/types';
import { formatNumber } from 'helpers/number';
import { compareNumber, compareString, localeCompare } from 'helpers/sort';
import { getPodcastImageUrl } from 'helpers/urls';
import {
    type AuDemoPlusCharacteristicHeader,
    type AuDemoPlusProgramCharacteristicHeader,
    type UsDemoPlusCharacteristicHeader,
    type AuDemosPlusCsvCharacteristic,
    type UsDemosPlusCsvCharacteristic,
    getDemosPlusCharacteristicLabel,
    DemosPlusHeaderMessageMap,
    DemosPlusColumnKey,
} from 'models/report/demoPlus';
import {
    type ReportDemosPlusProgramUs,
    type ReportDemosPlusProgramAu,
    type ReportDemosPlusPodcastUs,
    type ReportDemosPlusPodcastAu,
    type Podcast,
    type RowCompanies,
} from 'report/getReport';
import { theme } from 'styles/mui-theme';

export type TableColumnPdfCell =
    | string
    | {
          columnKey?: DemosPlusColumnKey;
          value: string;
          style: {
              textColor?: string;
              horizontalAlign?: 'left' | 'center' | 'right';
          };
      };

export type TableColumnPdf<Row> = {
    headerMessageDescriptor: MessageDescriptor;
    renderCellPdf: (row: Row, intl: IntlShape) => TableColumnPdfCell;
};

export type TableColumn<Row> = TableColumnPdf<Row> & {
    align: TableCellProps['align'] | undefined;
    renderCell: (row: Row, filter?: Partial<Record<string, FilterState>>) => React.ReactNode;
    sortAscendingFn: (a: Row, b: Row) => number;
    tooltip?: MessageDescriptor;
};

export const activePodcastsTableColumn: TableColumn<{ activePodcasts: number }> = {
    headerMessageDescriptor: defineMessage({
        defaultMessage: 'Active podcasts',
        description: 'Table column header for active podcasts',
    }),
    align: 'right',
    renderCell: (row) => <ActivePodcasts value={row.activePodcasts} />,
    renderCellPdf: (row, intl) => {
        return {
            value: intl.formatNumber(row.activePodcasts) + `${row.activePodcasts === 500 ? '+' : ''} `,
            style: { horizontalAlign: 'right' },
        };
    },
    sortAscendingFn: (a, b) => compareNumber(a.activePodcasts, b.activePodcasts),
};

export const averageWeeklyDownloadsTableColumn: TableColumn<{ averageWeeklyDownloads: number }> = {
    headerMessageDescriptor: defineMessage({
        defaultMessage: 'Avg weekly downloads',
        description: 'Table column header for average weekly downloads',
    }),
    align: 'right',
    renderCell: (row) => <FormattedNumber value={row.averageWeeklyDownloads} />,
    renderCellPdf: (row, intl) => {
        return {
            value: intl.formatNumber(row.averageWeeklyDownloads),
            style: { horizontalAlign: 'right' },
        };
    },
    sortAscendingFn: (a, b) => compareNumber(a.averageWeeklyDownloads, b.averageWeeklyDownloads),
};

export const averageWeeklyUsersTableColumn: TableColumn<{ averageWeeklyUsers: number }> = {
    headerMessageDescriptor: defineMessage({
        defaultMessage: 'Avg weekly users',
        description: 'Table column header for average weekly users',
    }),
    align: 'right',
    renderCell: (row) => <FormattedNumber value={row.averageWeeklyUsers} />,
    renderCellPdf: (row, intl) => {
        return {
            value: intl.formatNumber(row.averageWeeklyUsers),
            style: { horizontalAlign: 'right' },
        };
    },
    sortAscendingFn: (a, b) => compareNumber(a.averageWeeklyUsers, b.averageWeeklyUsers),
};

export const monthlyDownloadsTableColumn: TableColumn<{ monthlyDownloads: number }> = {
    headerMessageDescriptor: defineMessage({
        defaultMessage: 'Monthly downloads',
        description: 'Table column header for monthly downloads',
    }),
    align: 'right',
    renderCell: (row) => <FormattedNumber value={row.monthlyDownloads} />,
    renderCellPdf: (row, intl) => {
        return {
            value: intl.formatNumber(row.monthlyDownloads),
            style: { horizontalAlign: 'right' },
        };
    },
    sortAscendingFn: (a, b) => compareNumber(a.monthlyDownloads, b.monthlyDownloads),
};

export const monthlyListenersTableColumn: TableColumn<{ monthlyListeners: number }> = {
    headerMessageDescriptor: defineMessage({
        defaultMessage: 'Monthly listeners',
        description: 'Table column header for monthly listeners',
    }),
    align: 'right',
    renderCell: (row) => <FormattedNumber value={row.monthlyListeners} />,
    renderCellPdf: (row, intl) => {
        return {
            value: intl.formatNumber(row.monthlyListeners),
            style: { horizontalAlign: 'right' },
        };
    },
    sortAscendingFn: (a, b) => compareNumber(a.monthlyListeners, b.monthlyListeners),
};

export const monthlyReachTableColumn: TableColumn<{ monthlyReach: number }> = {
    headerMessageDescriptor: defineMessage({
        defaultMessage: 'Monthly Reach (%)',
        description: 'Table column header for monthly reach',
    }),
    align: 'right',
    renderCell: (row) => `${row.monthlyReach ? formatNumber(row.monthlyReach) : '-'}%`,
    renderCellPdf: (row, intl) => {
        return {
            value: `${intl.formatNumber(row.monthlyReach)}%`,
            style: { horizontalAlign: 'right' },
        };
    },
    sortAscendingFn: (a, b) => compareNumber(a.monthlyReach, b.monthlyReach),
};

export const networkTableColumn: TableColumn<{ networks: RowCompanies }> = {
    headerMessageDescriptor: defineMessage({
        defaultMessage: 'Network',
        description: 'Table column header for network',
    }),
    align: undefined,
    renderCell: (row) => row.networks.name,
    renderCellPdf: (row) => row.networks.name,
    sortAscendingFn: (a, b) => compareString(a.networks.name, b.networks.name),
};

export const podcastNameTableColumn: TableColumn<{
    podcast: Podcast;
}> = {
    headerMessageDescriptor: defineMessage({
        defaultMessage: 'Podcast',
        description: 'Table column header for podcast',
    }),
    align: undefined,
    renderCell: ({ podcast }) => {
        return (
            <div
                css={{
                    display: 'flex',
                    alignItems: 'center',
                }}
            >
                <PodcastImage podcast={podcast} />
                <span>{podcast.title}</span>
            </div>
        );
    },
    renderCellPdf: ({ podcast }) => podcast.title,
    sortAscendingFn: (a, b) => compareString(a.podcast.title, b.podcast.title),
};

export const podcastTypeTableColumn: TableColumn<{ podcastType: PodcastType }> = {
    headerMessageDescriptor: defineMessage({
        defaultMessage: 'Podcast Type',
        description: 'Table column header for podcast type',
    }),
    align: undefined,
    renderCell: (row) => formatPodcastTypeDisplay(row.podcastType),
    renderCellPdf: (row) => formatPodcastTypeDisplay(row.podcastType),
    sortAscendingFn: (a, b) => compareString(a.podcastType, b.podcastType),
};

export const podcastNameWithPublisherTableColumn: TableColumn<{
    podcast: Podcast;
    publishers: RowCompanies;
}> = {
    headerMessageDescriptor: podcastNameTableColumn.headerMessageDescriptor,
    align: podcastNameTableColumn.align,
    renderCell: ({ podcast, publishers }) => {
        return (
            <div
                css={{
                    display: 'flex',
                    alignItems: 'center',
                }}
            >
                <PodcastImage podcast={podcast} />
                <div>
                    <div>{podcast.title}</div>
                    <div
                        css={{
                            color: theme.tritonColors.gray,
                        }}
                    >
                        {publishers.name}
                    </div>
                </div>
            </div>
        );
    },
    renderCellPdf: ({ podcast, publishers }) => `${podcast.title} (${publishers.name})`,
    sortAscendingFn: podcastNameTableColumn.sortAscendingFn,
};

export const publisherTableColumn: TableColumn<{ publishers: RowCompanies }> = {
    headerMessageDescriptor: defineMessage({
        defaultMessage: 'Publisher',
        description: 'Table column header for publisher',
    }),
    align: undefined,
    renderCell: (row) => {
        return row.publishers.name;
    },
    renderCellPdf: (row) => row.publishers.name,
    sortAscendingFn: (a, b) => compareString(a.publishers.name, b.publishers.name),
};

export const publishersTableColumn: TableColumn<{ publishers: RowCompanies }> = {
    headerMessageDescriptor: defineMessage({
        defaultMessage: 'Publishers represented',
        description: 'Table column header for publishers represented',
    }),
    align: undefined,
    renderCell: (row) => {
        return row.publishers.name;
    },
    renderCellPdf: (row) => row.publishers.name,
    sortAscendingFn: (a, b) => compareString(a.publishers.name, b.publishers.name),
};

export const rankTableColumn: TableColumn<{ rank: number; rankChange: RankChange }> = {
    headerMessageDescriptor: defineMessage({
        defaultMessage: 'Rank',
        description: 'Table column header for rank',
    }),
    align: undefined,
    renderCell: (row) => {
        return (
            <div
                css={{
                    display: 'flex',
                    justifyContent: 'space-between',
                }}
            >
                <div css={{ fontSize: '1.3em' }}>{row.rank}</div>
                <RankChangeComponent rankChange={row.rankChange} />
            </div>
        );
    },
    renderCellPdf: (row, intl) => {
        return {
            value: intl.formatNumber(row.rank),
            style: { horizontalAlign: 'center' },
        };
    },
    sortAscendingFn: (a, b) => compareNumber(a.rank, b.rank),
};

export const salesNetworkTableColumn: TableColumn<{ networks: RowCompanies }> = {
    headerMessageDescriptor: defineMessage({
        defaultMessage: 'Sales network',
        description: 'Table column header for sales network',
    }),
    align: undefined,
    renderCell: (row) => row.networks.name,
    renderCellPdf: (row) => row.networks.name,
    sortAscendingFn: (a, b) => compareString(a.networks.name, b.networks.name),
};

export const salesRepresentativeTableColumn: TableColumn<{ salesRepresentatives: RowCompanies }> = {
    headerMessageDescriptor: defineMessage({
        defaultMessage: 'Sales representation',
        description: 'Table column header for sales representation',
    }),
    align: undefined,
    renderCell: (row) => {
        return row.salesRepresentatives.name;
    },
    renderCellPdf: (row) => row.salesRepresentatives.name,
    sortAscendingFn: (a, b) => compareString(a.salesRepresentatives.name, b.salesRepresentatives.name),
};

// Duplicate of salesRepresentativeTableColumn, but with a different label. Had to update the table column label of
// the Australian top podcast rankers [ jira-issue: DNA-577], but the label is shared. We will remove this duplicate if we want this change globally.
export const salesRepresentativeTableColumnAu: TableColumn<{ salesRepresentatives: RowCompanies }> = {
    headerMessageDescriptor: defineMessage({
        defaultMessage: 'Representation',
        description: 'Table column header for sales representation',
    }),
    align: undefined,
    renderCell: (row) => {
        return row.salesRepresentatives.name;
    },
    renderCellPdf: (row) => row.salesRepresentatives.name,
    sortAscendingFn: (a, b) => compareString(a.salesRepresentatives.name, b.salesRepresentatives.name),
};

export const categoryTableColumn: TableColumn<{ category: string | null }> = {
    headerMessageDescriptor: defineMessage({
        defaultMessage: 'Category',
        description: 'Table column header for category',
    }),
    align: 'right',
    renderCell: (row) => row.category,
    renderCellPdf: (row) => {
        return {
            value: row.category ? row.category : '-',
            style: { horizontalAlign: 'left' },
        };
    },
    sortAscendingFn: (a, b) => compareString(a.category ?? '', b.category ?? ''),
};

type DemosPlusProgramRow = ReportDemosPlusProgramUs['rows'][number] | ReportDemosPlusProgramAu['rows'][number];

// This is for both ReportTableDemosPlusProgramUs and ReportTableDemosPlusProgramAu
export const characteristicsTableColumn: TableColumn<DemosPlusProgramRow> = {
    headerMessageDescriptor: defineMessage({
        defaultMessage: 'Audience Characteristics',
        description: 'Table column header for audience characteristics',
    }),
    align: 'left',
    renderCell: (row, filter) => {
        const filterSelections: string[] = [];

        // Adding filter here for special demand for Demos+ Top Indexing Programs by Audience Composition Report
        // To only show the characteristics that are selected in the filter in the characteristics column.
        if (filter && filter['characteristics'] && filter['characteristics'].array.length > 0) {
            filterSelections.push(...filter['characteristics'].array);
        }

        const characteristics: KeyLabelValue[] = row.characteristics.map((key) => {
            const label = getDemosPlusCharacteristicLabel(key) ?? key;

            return {
                keylabel: { key, label },
                value: undefined,
            };
        });

        return (
            <Box
                css={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0.5em',
                }}
            >
                {characteristics.map((characteristic) => {
                    if (filterSelections.length > 0 && !filterSelections.includes(characteristic.keylabel.label)) {
                        return null;
                    }

                    return (
                        <Box
                            key={characteristic.keylabel.key}
                            css={{
                                display: 'flex',
                                justifyContent: 'space-between',
                            }}
                        >
                            <span>{characteristic.keylabel.label}</span>
                            {characteristic.value !== undefined && <span>{characteristic.value}</span>}
                        </Box>
                    );
                })}
            </Box>
        );
    },
    renderCellPdf: (row) => {
        const characteristics: KeyLabelValue[] = row.characteristics.map((key) => {
            const label = getDemosPlusCharacteristicLabel(key) ?? key;

            return {
                keylabel: { key, label: label || '' },
                value: undefined,
            };
        });

        return {
            columnKey: DemosPlusColumnKey.CHARACTERISTICS,
            value: characteristics
                .map((c) => `${c.keylabel.label}${c.value !== undefined ? `: ${c.value}` : ''}`)
                .join(', '),
            style: { horizontalAlign: 'left' },
        };
    },
    sortAscendingFn: (a, b) => {
        // sorting on characteristic arrays, uses sorted labels for comparison, should not be considered as a strict sort
        const aCategories = a.characteristics.map((key) => getDemosPlusCharacteristicLabel(key) ?? key).sort();

        const bCategories = b.characteristics.map((key) => getDemosPlusCharacteristicLabel(key) ?? key).sort();

        return localeCompare(aCategories.join(', '), bCategories.join(', '));
    },
};

// Only for PDF export
export const rankChangeTableColumn: TableColumnPdf<{ rankChange: RankChange }> = {
    headerMessageDescriptor: defineMessage({
        defaultMessage: 'Change',
        description: 'Table column header for rank change',
    }),
    renderCellPdf: (row) => {
        switch (row.rankChange.type) {
            case 'noChange': {
                return {
                    value: '-',
                    style: { horizontalAlign: 'center' },
                };
            }

            case 'debut': {
                return {
                    value: 'Debut',
                    style: { horizontalAlign: 'center' },
                };
            }

            case 'return': {
                return {
                    value: 'Return',
                    style: { horizontalAlign: 'center' },
                };
            }

            case 'up': {
                return {
                    value: '+' + row.rankChange.change,
                    style: { textColor: '#22c55e', horizontalAlign: 'center' },
                };
            }

            case 'down': {
                return {
                    value: row.rankChange.change.toString(),
                    style: { textColor: '#ef4444', horizontalAlign: 'center' },
                };
            }
        }
    },
};

export const newEpisodesTableColumn: TableColumnPdf<{ newEpisodes: number }> = {
    headerMessageDescriptor: defineMessage({
        defaultMessage: 'New episodes',
        description: 'Table column header for new episodes',
    }),
    renderCellPdf: (row, intl) => {
        return {
            value: intl.formatNumber(row.newEpisodes),
            style: { horizontalAlign: 'right' },
        };
    },
};

export const categoryTableColumnPdf: TableColumnPdf<{ category: string | null }> = {
    headerMessageDescriptor: defineMessage({
        defaultMessage: 'Category',
        description: 'Table column header for category',
    }),
    renderCellPdf: (row) => {
        return {
            value: row.category ? row.category : '-',
            style: { horizontalAlign: 'left' },
        };
    },
};

export function getDemosPlusCharacteristicColumn(
    key: AuDemoPlusCharacteristicHeader | AuDemoPlusProgramCharacteristicHeader | UsDemoPlusCharacteristicHeader,
    showTooltip = false,
    region: 'AU' | 'US' = 'AU',
): TableColumn<{ characteristics: AuDemosPlusCsvCharacteristic | UsDemosPlusCsvCharacteristic }> {
    const getTooltipMessage = (region: 'AU' | 'US') => {
        const messages = {
            US: defineMessage({
                defaultMessage:
                    'Index (Monthly Podcast pop 18+): Compares % of a podcast\'s audience against the overall United States monthly podcast listener population (age 18+). Eg: Index 125 "Category X is 25% more likely than average monthly podcast listener to do purchase a new car.',
                description: 'Tooltip for DemosPlus characteristic columns - US',
            }),
            AU: defineMessage({
                defaultMessage:
                    'Index (Monthly Podcast pop 18+): Compares % of a podcast\'s audience against the overall Australian monthly podcast listener population (age 18+). Eg: Index 125 "Category X is 25% more likely than average monthly podcast listener to do purchase a new car.',
                description: 'Tooltip for DemosPlus characteristic columns - AU',
            }),
        };

        return messages[region];
    };

    return {
        headerMessageDescriptor: DemosPlusHeaderMessageMap[key],
        align: 'right',
        renderCell: (row) =>
            formatNumber(row.characteristics[key as keyof typeof row.characteristics] ?? 0, { decimals: 0 }),
        renderCellPdf: (row) => ({
            value: formatNumber(row.characteristics[key as keyof typeof row.characteristics] ?? 0, { decimals: 0 }),
            style: { horizontalAlign: 'right' },
        }),
        sortAscendingFn: (a, b) =>
            compareNumber(
                a.characteristics[key as keyof typeof a.characteristics] ?? 0,
                b.characteristics[key as keyof typeof b.characteristics] ?? 0,
            ),
        tooltip: showTooltip ? getTooltipMessage(region) : undefined,
    };
}

export function getDemosPlusPdfCharacteristicColumn(
    key: AuDemoPlusCharacteristicHeader | AuDemoPlusProgramCharacteristicHeader | UsDemoPlusCharacteristicHeader,
): TableColumnPdf<{ characteristics: AuDemosPlusCsvCharacteristic | UsDemosPlusCsvCharacteristic }> {
    return {
        headerMessageDescriptor: DemosPlusHeaderMessageMap[key],
        renderCellPdf: (row) => {
            return {
                value: formatNumber(row.characteristics[key as keyof typeof row.characteristics] ?? 0, { decimals: 0 }),
                style: { horizontalAlign: 'right' },
            };
        },
    };
}

type KeyLabel = {
    key: UsDemoPlusCharacteristicHeader | AuDemoPlusCharacteristicHeader | AuDemoPlusProgramCharacteristicHeader;
    label: string;
};

type KeyLabelValue = {
    keylabel: KeyLabel;
    value: string | number | undefined;
};

export function getDemoPlusColumnHeader(
    rows: ReportDemosPlusPodcastUs['rows'] | ReportDemosPlusPodcastAu['rows'],
): KeyLabel[] {
    if (!rows.length) {
        return [];
    }
    const columnOptions = Object.keys(rows[0].characteristics);

    const newArr = columnOptions
        .reduce((acc: KeyLabel[], key: string) => {
            if (key in DemosPlusHeaderMessageMap) {
                const label = getDemosPlusCharacteristicLabel(key) ?? key;

                const obj = {
                    key: key as UsDemoPlusCharacteristicHeader | AuDemoPlusCharacteristicHeader,
                    label,
                };
                acc.push(obj);
            }

            return acc;
        }, [])
        .sort((a, b) => localeCompare(a.label, b.label));

    return newArr;
}

export const formatPodcastTypeDisplay = (value: string): string => {
    switch (value) {
        case 'podcast':
            return 'Podcast';
        case 'audioOnDemand':
            return 'Audio On Demand';
        default:
            return 'N/A';
    }
};

function PodcastImage({ podcast }: { podcast: Podcast }) {
    return (
        <Box
            width={40}
            height={40}
            css={{
                background: podcast.imageDominantColor,
                marginRight: 10,
                flexShrink: 0,
                [theme.breakpoints.down('md')]: {
                    width: 32,
                    height: 32,
                },
            }}
        >
            <ImageFade src={getPodcastImageUrl(podcast, 'thumb')} alt={podcast.title} width="100%" height="100%" />
        </Box>
    );
}
